import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, FileType, FileCode2 } from "lucide-react";
import { money, SUPPLIER_DOC_TYPES, STATUS_COLORS } from "@/lib/constants";

/**
 * SupplierDocumentsCard — se embebe en CompraDetalle, CuentaPorPagarDetalle y DevolucionDetalle.
 * Muestra documentos administrativos vinculados a la entidad (OC / CxP / Devolución).
 * Solo lectura. Con nota clara para NC (evidencia documental).
 */
export function SupplierDocumentsCard({
  entityType,        // "po" | "ap" | "return"
  entityId,
  refreshKey = 0,
  testIdPrefix = "linked-docs",
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) return;
    let alive = true;
    const url = entityType === "po"
      ? `/purchase-orders/${entityId}/documents`
      : entityType === "ap"
        ? `/accounts-payable/${entityId}/documents`
        : `/purchase-returns/${entityId}/documents`;
    setLoading(true);
    api.get(url)
      .then(({ data }) => { if (alive) setItems(data || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [entityType, entityId, refreshKey]);

  if (loading || items.length === 0) return null;

  const hasNC = items.some((d) => d.doc_type === "nota_credito" && d.status === "activo");
  const totalCredited = items
    .filter((d) => d.doc_type === "nota_credito" && d.status === "activo")
    .reduce((acc, d) => acc + Number(d.credited_amount || 0), 0);

  return (
    <Card className="border-border" data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="border-b border-border pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" /> Documentos del proveedor ligados ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-2">
        {items.map((d) => {
          const t = SUPPLIER_DOC_TYPES.find((x) => x.value === d.doc_type);
          const tone = d.doc_type === "factura" ? "blue"
            : d.doc_type === "nota_credito" ? "amber"
            : "slate";
          const badgeCls = d.status === "cancelado" ? STATUS_COLORS.red : STATUS_COLORS[tone];
          return (
            <div
              key={d.id}
              className="flex items-center justify-between border border-border rounded-md p-2 text-sm hover:bg-slate-50 cursor-pointer"
              onClick={() => navigate(`/documentos-proveedores/${d.id}`)}
              data-testid={`${testIdPrefix}-row-${d.id}`}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={badgeCls}>
                  {d.status === "cancelado" ? "Cancelado · " : ""}{t?.label || d.doc_type}
                </Badge>
                {(d.external_serie || d.external_folio) && (
                  <span className="mono text-xs">
                    {[d.external_serie, d.external_folio].filter(Boolean).join("-")}
                  </span>
                )}
                {d.issue_date && <span className="text-xs text-muted-foreground">{d.issue_date}</span>}
                <div className="flex items-center gap-1">
                  {d.pdf_url && <FileType className="h-3.5 w-3.5 text-red-600" />}
                  {d.xml_url && <FileCode2 className="h-3.5 w-3.5 text-blue-600" />}
                </div>
              </div>
              <div className="text-xs mono text-right">
                {d.doc_type === "nota_credito"
                  ? <span className="text-amber-700">−{money(d.credited_amount)} <span className="text-[10px]">(informativo)</span></span>
                  : money(d.total)}
              </div>
            </div>
          );
        })}
        {entityType === "return" && hasNC && (
          <div className="mt-3 border-t border-border pt-3 text-xs text-amber-800 bg-amber-50 rounded-md p-2" data-testid={`${testIdPrefix}-nc-note`}>
            <strong>Notas de crédito ligadas:</strong> {money(totalCredited)} en total (informativo).
            El sistema <strong>no ajusta automáticamente</strong> la CxP con base en las NC. Aplica el crédito con tu proveedor por fuera.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
